import React, { ReactElement, useState, useEffect } from "react"
import { Form, Button } from "semantic-ui-react"
import styled from "styled-components"
import {
  DataVisualizationRequest,
  DataVisualizationRequestConfig,
  DataVisualizationRequestType,
  FilterValues,
  InputParameterType,
  CompanyOption,
  DataVisualizationInitialData,
  DataVisualizationGranularity,
  DataVisualizationGroupingType,
  CompanyAddressOption,
  CompanyAddressZoneOption,
} from "types/data-visualization.types"
import requestsConfig from "config/data-visualization-requests.json"
import CompanySelect from "./CompanySelect"
import PeriodInput from "./PeriodInput"
import ShipperInput from "./ShipperInput"
import GranularitySelect from "./GranularitySelect"

interface DynamicInputFormProps {
  selectedRequestType: DataVisualizationRequestType
  onSubmit: (request: DataVisualizationRequest) => void
  isLoading?: boolean
  initialData?: DataVisualizationInitialData | null
}

const FormContainer = styled.div`
  margin-bottom: 20px;
`

const FormRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  align-items: flex-start;
`

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
`

const RightSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-top: 20px;
`

const PeriodSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`

const SubmitButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`

export default function DynamicInputForm({
  selectedRequestType,
  onSubmit,
  isLoading = false,
  initialData,
}: DynamicInputFormProps): ReactElement {
  const [filters, setFilters] = useState<FilterValues>({})
  const [selectedCompany, setSelectedCompany] = useState<CompanyOption | undefined>()
  const [selectedCompanyAddressZone, setSelectedCompanyAddressZone] = useState<CompanyAddressZoneOption | undefined>()
  const [selectedCompanyAddress, setSelectedCompanyAddress] = useState<CompanyAddressOption | undefined>()
  // const [selectedZipcodes, setSelectedZipcodes] = useState<ZipcodeOption[]>([])
  const [selectedGranularity, setSelectedGranularity] = useState<DataVisualizationGranularity>(
    DataVisualizationGranularity.MONTHLY,
  )
  const [selectedGroupingType, setSelectedGroupingType] = useState<DataVisualizationGroupingType>(
    DataVisualizationGroupingType.PERIOD,
  )

  const request = (requestsConfig as any)[selectedRequestType] as DataVisualizationRequestConfig

  // Reset form when request type changes
  useEffect(() => {
    setFilters({})
    setSelectedCompany(undefined)
    setSelectedCompanyAddressZone(undefined)
    setSelectedCompanyAddress(undefined)
    //setSelectedZipcodes(undefined)
    setSelectedGranularity(DataVisualizationGranularity.MONTHLY)
    setSelectedGroupingType(DataVisualizationGroupingType.PERIOD)
  }, [selectedRequestType])

  const handleInputChange = (parameterType: InputParameterType, value: any) => {
    const newFilters = { ...filters }

    switch (parameterType) {
      case InputParameterType.COMPANY:
        setSelectedCompany(value)
        setSelectedCompanyAddressZone(undefined)
        setSelectedCompanyAddress(undefined)
        // setSelectedZipcodes(undefined)
        newFilters.company = value?.value
        delete newFilters.companyAddress
        delete newFilters.companyAddressZone
        delete newFilters.zipcodes
        break
      case InputParameterType.COMPANY_ADDRESS:
        setSelectedCompanyAddress(value)
        // setSelectedZipcodes(undefined)
        newFilters.companyAddress = value?.value
        delete newFilters.zipcodes
        break
      case InputParameterType.COMPANY_ADDRESS_ZONE:
        setSelectedCompanyAddressZone(value)
        setSelectedCompanyAddress(undefined)
        // setSelectedZipcodes(undefined)
        newFilters.companyAddressZone = value?.value
        delete newFilters.companyAddress
        delete newFilters.zipcodes
        break
      case InputParameterType.PERIOD:
        newFilters.period = value
        break
      case InputParameterType.ZIPCODE:
        newFilters.zipcodes = value
        break
      case InputParameterType.SHIPPER:
        newFilters.shipper = value
        break
    }

    setFilters(newFilters)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const request: DataVisualizationRequest = {
      type: selectedRequestType,
      filters: {
        shipperName: filters.shipper,
        companyId: selectedCompany?.value,
        companyAddressId: selectedCompanyAddress?.value,
        companyAddressZoneId: selectedCompanyAddressZone?.value,
      },
      period: {
        startDate: filters.period?.startDate,
        endDate: filters.period?.endDate,
      },
      groupingType: selectedGroupingType,
      granularity: selectedGranularity,
    }

    onSubmit(request)
  }

  const isFormValid = () => {
    // Check if all mandatory inputs are filled
    return request.mandatoryInputs.every(input => {
      switch (input.type) {
        case InputParameterType.COMPANY:
          return !input.required || selectedCompany
        case InputParameterType.COMPANY_ADDRESS:
          return !input.required || selectedCompanyAddress
        case InputParameterType.COMPANY_ADDRESS_ZONE:
          return !input.required || selectedCompanyAddressZone
        case InputParameterType.PERIOD:
          return !input.required || filters.period
        case InputParameterType.ZIPCODE:
          return !input.required || (filters.zipcodes && filters.zipcodes.length > 0)
        case InputParameterType.SHIPPER:
          return !input.required || filters.shipper
        default:
          return true
      }
    })
  }

  if (!request) {
    return <div>Configuration de requête non trouvée</div>
  }

  return (
    <FormContainer>
      <Form onSubmit={handleSubmit}>
        <FormRow>
          <LeftSection>
            <div style={{ display: "flex", flexDirection: "row", gap: "5px" }}>
              <CompanySelect
                value={selectedCompany}
                onChange={value => handleInputChange(InputParameterType.COMPANY, value)}
                placeholder="Client"
                label="Transporteur"
                required={true}
                height="35px"
                options={initialData?.companies.map(company => ({ value: company.id, label: company.name })) || []}
              />

              <CompanySelect
                value={selectedCompanyAddressZone}
                onChange={value => handleInputChange(InputParameterType.COMPANY_ADDRESS_ZONE, value)}
                placeholder="Zone géographique"
                required={false}
                height="35px"
                label="Zone géographique"
                disabled={!selectedCompany?.value}
                options={
                  initialData?.companyAddressZones
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(companyAddressZone => ({
                      value: companyAddressZone.id,
                      label: companyAddressZone.name,
                    })) || []
                }
              />

              <CompanySelect
                value={selectedCompanyAddress}
                onChange={value => handleInputChange(InputParameterType.COMPANY_ADDRESS, value)}
                placeholder="Agence"
                label="Agence"
                required={false}
                height="35px"
                disabled={!selectedCompany?.value}
                options={
                  initialData?.companyAddresses
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .filter(companyAddress => {
                      if (selectedCompanyAddressZone?.value) {
                        return companyAddress.zoneId === selectedCompanyAddressZone?.value
                      }
                      return companyAddress.companyId === selectedCompany?.value
                    })
                    .map(companyAddress => ({
                      value: companyAddress.id,
                      label: companyAddress.name,
                    })) || []
                }
              />
            </div>

            <ShipperInput
              value={filters.shipper}
              onChange={value => handleInputChange(InputParameterType.SHIPPER, value)}
              placeholder="Chargeur"
              label="Chargeur"
              required={false}
              height="35px"
              disabled={!selectedCompany?.value}
            />
          </LeftSection>

          <RightSection>
            <PeriodSection>
              <PeriodInput
                value={filters.period}
                onChange={value => handleInputChange(InputParameterType.PERIOD, value)}
                required={true}
                height="35px"
                updateGranularity={setSelectedGranularity}
              />
            </PeriodSection>
            <div style={{ flex: 1, paddingTop: "10px" }}>
              <GranularitySelect
                value={selectedGranularity}
                onChange={setSelectedGranularity}
                placeholder="Granularité"
                width="120px"
                height="30px"
              />
            </div>
          </RightSection>
        </FormRow>

        <SubmitButtonContainer>
          <Button
            type="submit"
            primary
            disabled={!isFormValid() || isLoading}
            loading={isLoading}
            size="large"
            style={{
              minWidth: "200px",
              borderRadius: "8px",
              fontWeight: "600",
            }}
          >
            Lancer l&apos;analyse
          </Button>
        </SubmitButtonContainer>
      </Form>
    </FormContainer>
  )
}
